import React, { useEffect, useRef } from "react";
import { Col, Row } from "react-bootstrap";

const ABFooter = () => {
    const rollRef = useRef(null);

    useEffect(() => {
        const roll = rollRef.current;

        if (!roll) return;

        const lines = Array.from(
            roll.querySelectorAll(".abf-line")
        );

        if (lines.length < 2) return;

        let current = 0;
        let timer = null;
        let transitionTimer = null;

        // Text changes every 2.8 seconds
        const dwell = 5000;

        const reducedMotion = window
            .matchMedia("(prefers-reduced-motion: reduce)")
            .matches;

        const setRollHeight = () => {
            let tallest = 0;

            lines.forEach((line) => {
                tallest = Math.max(
                    tallest,
                    line.scrollHeight
                );
            });

            roll.style.minHeight = `${Math.max(
                tallest,
                48
            )}px`;
        };

        const showLine = (next) => {
            if (next === current) return;

            const previous = current;

            current = next;

            lines[previous].classList.remove(
                "is-live"
            );

            lines[previous].classList.add(
                "is-out"
            );

            lines[current].classList.remove(
                "is-out"
            );

            lines[current].classList.add(
                "is-live"
            );

            if (transitionTimer) {
                window.clearTimeout(
                    transitionTimer
                );
            }

            transitionTimer =
                window.setTimeout(() => {
                    lines[previous].classList.remove(
                        "is-out"
                    );
                }, 700);
        };

        setRollHeight();

        if (!reducedMotion) {
            timer = window.setInterval(() => {
                showLine(
                    (current + 1) % lines.length
                );
            }, dwell);
        }

        window.addEventListener(
            "resize",
            setRollHeight
        );

        return () => {
            if (timer) {
                window.clearInterval(timer);
            }

            if (transitionTimer) {
                window.clearTimeout(
                    transitionTimer
                );
            }

            window.removeEventListener(
                "resize",
                setRollHeight
            );
        };
    }, []);

    const handleCocoClick = () => {
        const launcher =
            document.querySelector(
                '[id*="collect-chat"] button, [class*="collect-chat"] button'
            );

        const frame =
            document.querySelector(
                'iframe[src*="collect.chat"]'
            );

        const reducedMotion =
            window
                .matchMedia(
                    "(prefers-reduced-motion: reduce)"
                )
                .matches;

        if (launcher) {
            launcher.click();
            return;
        }

        if (frame) {
            frame.scrollIntoView({
                behavior: reducedMotion
                    ? "auto"
                    : "smooth",
                block: "center",
            });

            frame.focus();
        }
    };

    const footerLines = [
        "Most media houses buy attention. Few know whose attention is worth buying.",
        "A low CPL is expensive when the lead cannot afford you.",
        "Luxury is not an interest you select on Meta.",
        "More media only makes weak positioning fail faster.",
        "Retargeting the wrong audience only wastes the budget twice.",
        "Anyone can spend the budget. Strategy decides where not to spend it.",
        "A luxury publisher does not automatically make it to a luxury media plan.",
        "Performance without positioning is just promotion with better tracking.",
        "Most firms rent audience intelligence. We built OSIDE.",
        "Media was never the whole answer. That is why A&B begins with strategy.",
        "We built A&B for the gap between media delivered and business achieved.",
    ];

    return (
        <>
            <div
                className="abf"
                data-abf-footer
            >

                {/* =========================
                    STORY
                ========================= */}

                <section
                    className="abf-story"
                    aria-label="A&B Luxury perspective"
                >
                    <div className="abf-story-inner">

                        <div
                            className="abf-roll"
                            ref={rollRef}
                            aria-live="polite"
                            aria-atomic="true"
                        >
                            {footerLines.map(
                                (line, index) => (
                                    <span
                                        key={index}
                                        className={`abf-line ${index === 0
                                            ? "is-live"
                                            : ""
                                            }`}
                                    >
                                        {line}
                                    </span>
                                )
                            )}
                        </div>

                        <div className="abf-intro">

                            <p>
                                <strong>
                                    A&B LUXURY
                                </strong>{" "}
                                is a luxury marketing
                                consultancy and digital
                                media company working
                                with premium, luxury and
                                heritage brands. We help
                                brands strengthen
                                visibility, reach affluent
                                audiences and drive
                                measurable business
                                growth.
                            </p>

                            <p>
                                Our services are designed
                                specifically for luxury
                                businesses and include{" "}
                                <a href="/luxury-digital-branding-agency.html">
                                    luxury brand strategy
                                </a>
                                , affluent audience
                                planning,{" "}
                                <a href="/media-buying-agency-luxury.html">
                                    premium media planning
                                    and buying
                                </a>
                                , performance marketing,{" "}
                                <a href="/luxury-seo-agency.html">
                                    SEO
                                </a>{" "}
                                and GEO optimisation,{" "}
                                <a href="/luxury-e-commerce-agency.html">
                                    ecommerce growth
                                </a>
                                ,{" "}
                                <a href="/retail-digital-marketing-agency-luxury.html">
                                    retail footfall
                                    campaigns
                                </a>
                                ,{" "}
                                <a href="/digital-lead-generation-agency-luxury.html">
                                    high-value customer
                                    acquisition and
                                    qualified lead
                                    generation
                                </a>
                                .
                            </p>

                        </div>
                    </div>
                </section>


                {/* =========================
                    WHITE BAND
                ========================= */}

                <section
                    className="abf-band"
                    aria-label="Footer links and credentials"
                >
                    <div className="abf-band-inner">

                        {/* PRIVACY */}

                        <a
                            className="abf-privacy"
                            href="/privacy-policy.html"
                        >
                            Privacy Policy
                        </a>


                        <span
                            className="abf-divider"
                            aria-hidden="true"
                        />


                        {/* ASSOCIATIONS */}

                        <div className="abf-group">

                            <span className="abf-label">
                                Associations &
                                Certifications
                            </span>

                            <div
                                className="abf-icons abf-association-icons"
                                aria-label="A&B Luxury associations and certifications"
                            >

                                <span
                                    className="abf-icon abf-static-icon"
                                    role="img"
                                    aria-label="Global association"
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <circle
                                            cx="12"
                                            cy="12"
                                            r="8"
                                        />

                                        <path d="M4 12h16" />

                                        <path d="M12 4c2.2 2.2 3.2 4.9 3.2 8S14.2 17.8 12 20" />

                                        <path d="M12 4c-2.2 2.2-3.2 4.9-3.2 8S9.8 17.8 12 20" />
                                    </svg>
                                </span>


                                <span
                                    className="abf-icon abf-static-icon"
                                    role="img"
                                    aria-label="Media certification"
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <circle
                                            cx="12"
                                            cy="12"
                                            r="8"
                                        />

                                        <circle
                                            cx="12"
                                            cy="12"
                                            r="3"
                                        />

                                        <path d="M12 4v2" />
                                        <path d="M20 12h-2" />
                                        <path d="M12 20v-2" />
                                        <path d="M4 12h2" />
                                    </svg>
                                </span>


                                <span
                                    className="abf-icon abf-static-icon"
                                    role="img"
                                    aria-label="Verified certification"
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path d="M12 3.5 19 6v5.2c0 4.4-2.7 7.7-7 9.3-4.3-1.6-7-4.9-7-9.3V6l7-2.5Z" />

                                        <path d="m8.8 12 2.1 2.1 4.4-4.4" />
                                    </svg>
                                </span>


                                <span
                                    className="abf-icon abf-static-icon"
                                    role="img"
                                    aria-label="Partner network"
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <circle
                                            cx="12"
                                            cy="5"
                                            r="1.7"
                                        />

                                        <circle
                                            cx="6"
                                            cy="12"
                                            r="1.7"
                                        />

                                        <circle
                                            cx="18"
                                            cy="12"
                                            r="1.7"
                                        />

                                        <circle
                                            cx="12"
                                            cy="19"
                                            r="1.7"
                                        />

                                        <path d="m12 6.7-4.8 4.1" />
                                        <path d="m12 6.7 4.8 4.1" />
                                        <path d="M7.7 12h8.6" />
                                        <path d="m7.2 13.2 3.8 4.5" />
                                        <path d="m16.8 13.2-3.8 4.5" />
                                    </svg>
                                </span>

                            </div>
                        </div>


                        <span
                            className="abf-divider"
                            aria-hidden="true"
                        />


                        {/* SOCIALS */}

                        <div className="abf-group">

                            <span className="abf-label">
                                Our Socials
                            </span>

                            <div
                                className="abf-icons abf-social-icons"
                                aria-label="A&B Luxury social media"
                            >

                                {/* YOUTUBE */}

                                <a
                                    className="abf-icon"
                                    href="https://www.youtube.com/@anb_luxury/videos"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="A&B Luxury on YouTube"
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path d="M21 8.2a2.8 2.8 0 0 0-2-2C17.3 5.7 12 5.7 12 5.7s-5.3 0-7 .5a2.8 2.8 0 0 0-2 2A29 29 0 0 0 2.5 12 29 29 0 0 0 3 15.8a2.8 2.8 0 0 0 2 2c1.7.5 7 .5 7 .5s5.3 0 7-.5a2.8 2.8 0 0 0 2-2 29 29 0 0 0 .5-3.8A29 29 0 0 0 21 8.2Z" />

                                        <path
                                            className="abf-icon-fill"
                                            d="m10 9 5 3-5 3Z"
                                        />
                                    </svg>
                                </a>


                                {/* LINKEDIN */}

                                <a
                                    className="abf-icon"
                                    href="https://www.linkedin.com/company/anbluxury"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="A&B Luxury on LinkedIn"
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path
                                            className="abf-icon-fill"
                                            d="M6.2 8.1H3.3V20h2.9V8.1ZM4.75 3A1.75 1.75 0 1 0 4.75 6.5 1.75 1.75 0 0 0 4.75 3ZM8.8 8.1H11.6v1.6h.04c.39-.74 1.34-1.92 3.74-1.92 4 0 4.74 2.48 4.74 5.7V20h-2.9v-5.76c0-1.37-.03-3.13-1.91-3.13-1.91 0-2.2 1.49-2.2 3.03V20H8.8V8.1Z"
                                        />
                                    </svg>
                                </a>


                                {/* BLOG */}

                                <a
                                    className="abf-icon"
                                    href="/blog/"
                                    aria-label="A&B Luxury blog"
                                >
                                    <span className="abf-blog-mark">
                                        W
                                    </span>
                                </a>


                                {/* FACEBOOK */}

                                <a
                                    className="abf-icon"
                                    href="https://www.facebook.com/anbluxury/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="A&B Luxury on Facebook"
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path
                                            className="abf-icon-fill"
                                            d="M13.5 21v-8h2.7l.4-3h-3.1V8.1c0-.87.24-1.46 1.5-1.46h1.7V4a22 22 0 0 0-2.48-.13c-2.45 0-4.13 1.5-4.13 4.27V10H7.4v3h2.69v8h3.41Z"
                                        />
                                    </svg>
                                </a>


                                {/* INSTAGRAM */}

                                <a
                                    className="abf-icon"
                                    href="https://www.instagram.com/anb.luxury/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="A&B Luxury on Instagram"
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <rect
                                            x="4"
                                            y="4"
                                            width="16"
                                            height="16"
                                            rx="4"
                                        />

                                        <circle
                                            cx="12"
                                            cy="12"
                                            r="3.5"
                                        />

                                        <circle
                                            className="abf-icon-fill"
                                            cx="17.2"
                                            cy="6.9"
                                            r="1"
                                        />
                                    </svg>
                                </a>

                            </div>
                        </div>


                        {/* COCO */}

                        <button
                            className="abf-coco"
                            type="button"
                            onClick={handleCocoClick}
                            aria-label="Open Coco chatbot"
                        >
                            <span>
                                Coco
                            </span>

                            <svg
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                            >
                                <path d="M5 6.5h14v9H9l-4 3v-12Z" />
                            </svg>
                        </button>

                    </div>
                </section>


                {/* =========================
                    CLOSING
                ========================= */}

                <section
                    className="abf-closing"
                    aria-label="About A&B Luxury"
                >

                    <p className="abf-founded">

                        Founded in 2016 and led by{" "}

                        <a
                            href="https://www.instagram.com/syedmamoonhasan/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Syed H
                        </a>

                        , we{" "}

                        <a href="/work.html">
                            work
                        </a>{" "}

                        across luxury real estate,
                        fashion, automotive,
                        hospitality, wellness,
                        jewelry and lifestyle. Our
                        proprietary audience
                        intelligence platform, OSIDE,
                        helps brands reach HNI, UHNI
                        and affluent audiences through
                        focused digital campaigns.
                        A&B Luxury operates across
                        Mumbai,{" "}

                        <a href="/luxury-digital-agency-dubai.html">
                            Dubai
                        </a>
                        , Riyadh,{" "}

                        <a href="/luxury-digital-agency-london.html">
                            London
                        </a>
                        ,{" "}

                        <a href="/luxury-digital-agency-bangalore.html">
                            Bengaluru
                        </a>{" "}

                        and Brisbane.

                    </p>


                    <svg
                        className="abf-wordmark"
                        viewBox="0 0 1200 190"
                        preserveAspectRatio="xMidYMid meet"
                        role="img"
                        aria-label="A&B Luxury"
                    >
                        <text
                            x="600"
                            y="156"
                            textLength="1140"
                            lengthAdjust="spacing"
                            textAnchor="middle"
                        >
                            A&B LUXURY
                        </text>
                    </svg>

                   <br></br>
                   <br></br>

                    <Row className="align-items-center">
                        <Col lg="4" md="4" sm="12" xs="12">
                            <img
                                style={{ width: '100%' }}
                                src={`${process.env.PUBLIC_URL}/img/duns_qr.jpg`}
                                alt=""
                            />
                        </Col>
                        <Col lg="4" md="4" sm="12" xs="12">
                        </Col>

                        <Col
                            lg="4"
                            md="4"
                            sm="12"
                            xs="12"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                                justifyContent: 'flex-end'
                            }}
                        >
                            <p
                                className="abf-copy"
                                style={{ margin: 0 }}
                            >
                                © 2026 A&B Luxury
                            </p>
                        </Col>
                    </Row>
                </section>

            </div>


            {/* =========================
                CSS
            ========================= */}

            <style>{`

                @import url(
                    'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&display=swap'
                );


                /* =========================================
                   ROOT
                ========================================= */

                .abf {
                    --abf-black: #0a0a0a;
                    --abf-white: #ffffff;
                    --abf-serif:
                        'Cormorant Garamond',
                        Georgia,
                        serif;

                    width: 100vw;
                    max-width: 100vw;

                    margin:
                        0 calc(50% - 50vw);

                    overflow: hidden;

                    background:
                        var(--abf-black);

                    color:
                        var(--abf-white);

                    font-family:
                        var(--abf-serif);
                }


                .abf *,
                .abf *::before,
                .abf *::after {
                    box-sizing: border-box;

                    font-family:
                        var(--abf-serif);
                }


                /* =========================================
                   STORY
                ========================================= */

                .abf-story {
                    padding:
                        clamp(8rem, 10vw, 12rem)
                        2.5rem
                        clamp(9rem, 12vw, 14rem);

                    background:
                        var(--abf-black);

                    color:
                        var(--abf-white);

                    text-align:
                        center;
                }


                .abf-story-inner {
                    width:
                        min(100%, 1400px);

                    margin:
                        0 auto;
                }


                .abf-roll {
                    position:
                        relative;

                    min-height:
                        48px;

                    margin-bottom:
                        clamp(
                            6rem,
                            8vw,
                            9rem
                        );
                }


                .abf-line {
                    position:
                        absolute;

                    inset:
                        0;

                    display:
                        flex;

                    align-items:
                        flex-start;

                    justify-content:
                        center;

                    width:
                        100%;

                    color:
                        var(--abf-white);

                    font-size:
                        clamp(
                            1.8rem,
                            2.2vw,
                            3.25rem
                        );

                    font-weight:
                        400;

                    line-height:
                        1.18;

                    letter-spacing:
                        0.005em;

                    white-space:
                        nowrap;

                    opacity:
                        0;

                    transform:
                        translateY(0.45em);

                    transition:
                        opacity 0.65s ease,
                        transform 0.65s
                        cubic-bezier(
                            0.22,
                            0.61,
                            0.36,
                            1
                        );

                    pointer-events:
                        none;

                    will-change:
                        opacity,
                        transform;
                }


                .abf-line.is-live {
                    opacity:
                        1;

                    transform:
                        translateY(0);
                }


                .abf-line.is-out {
                    opacity:
                        0;

                    transform:
                        translateY(-0.45em);
                }


                /* =========================================
                   INTRO
                ========================================= */

                .abf-intro {
                    width:
                        min(100%, 1080px);

                    margin:
                        0 auto;
                }


                .abf-intro p {
                    margin:
                        0;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.88
                        );

                    font-size:
                        clamp(
                            1.15rem,
                            1.45vw,
                            1.45rem
                        );

                    font-weight:
                        400;

                    line-height:
                        1.72;
                }


                .abf-intro p + p {
                    margin-top:
                        2.4rem;
                }


                .abf-intro strong {
                    color:
                        var(--abf-white);

                    font-weight:
                        600;

                    letter-spacing:
                        0.12em;
                }


                .abf-intro a,
                .abf-founded a {
                    color:
                        inherit;

                    text-decoration:
                        underline;

                    text-decoration-thickness:
                        1px;

                    text-underline-offset:
                        0.18em;

                    text-decoration-color:
                        rgba(
                            255,
                            255,
                            255,
                            0.35
                        );

                    transition:
                        color 0.25s ease,
                        text-decoration-color
                        0.25s ease;
                }


                .abf-intro a:hover,
                .abf-founded a:hover {
                    color:
                        var(--abf-white);

                    text-decoration-color:
                        var(--abf-white);
                }


                /* =========================================
                   WHITE BAND
                ========================================= */

                .abf-band {
                    width:
                        100%;

                    padding:
                        2.8rem 2rem;

                    background:
                        #ffffff;

                    color:
                        #0a0a0a;

                    overflow:
                        hidden;
                }


                .abf-band-inner {
                    width:
                        min(100%, 1400px);

                    max-width:
                        100%;

                    margin:
                        0 auto;

                    display:
                        flex;

                    align-items:
                        center;

                    justify-content:
                        center;

                    gap:
                        1.8rem;

                    flex-wrap:
                        wrap;
                }


                /* =========================================
                   LABELS
                ========================================= */

                .abf-privacy,
                .abf-label {
                    flex:
                        0 0 auto;

                    color:
                        #0a0a0a;

                    font-size:
                        0.95rem;

                    font-weight:
                        500;

                    line-height:
                        1.2;

                    letter-spacing:
                        0.14em;

                    text-transform:
                        uppercase;

                    white-space:
                        nowrap;
                }


                .abf-privacy {
                    padding-bottom:
                        0.18rem;

                    border-bottom:
                        1px solid transparent;

                    text-decoration:
                        none;

                    transition:
                        border-color
                        0.25s ease;
                }


                .abf-privacy:hover {
                    border-bottom-color:
                        #0a0a0a;
                }


                /* =========================================
                   DIVIDER
                ========================================= */

                .abf-divider {
                    width:
                        1px;

                    height:
                        3rem;

                    flex:
                        0 0 1px;

                    background:
                        rgba(
                            10,
                            10,
                            10,
                            0.3
                        );
                }


                /* =========================================
                   GROUP
                ========================================= */

                .abf-group {
                    display:
                        flex;

                    align-items:
                        center;

                    gap:
                        1.1rem;

                    min-width:
                        0;
                }


                /* =========================================
                   ICON ROW
                ========================================= */

                .abf-icons {
                    display:
                        flex;

                    align-items:
                        center;

                    gap:
                        0.65rem;

                    flex-wrap:
                        nowrap;
                }


                /* =========================================
                   ICON CIRCLES
                ========================================= */

                .abf-band .abf-icon {
                    width:
                        2.85rem !important;

                    height:
                        2.85rem !important;

                    min-width:
                        2.85rem !important;

                    min-height:
                        2.85rem !important;

                    flex:
                        0 0 2.85rem !important;

                    display:
                        inline-flex !important;

                    align-items:
                        center !important;

                    justify-content:
                        center !important;

                    padding:
                        0 !important;

                    margin:
                        0 !important;

                    border:
                        1px solid #0a0a0a !important;

                    border-radius:
                        50% !important;

                    background:
                        #ffffff !important;

                    color:
                        #0a0a0a !important;

                    text-decoration:
                        none !important;

                    box-sizing:
                        border-box !important;

                    overflow:
                        hidden !important;

                    position:
                        relative;

                    z-index:
                        1;

                    transition:
                        background-color
                        0.25s ease,
                        color
                        0.25s ease,
                        transform
                        0.25s ease;
                }


                /* =========================================
                   SOCIAL SVG
                ========================================= */

                .abf-band .abf-social-icons
                .abf-icon svg {
                    display:
                        block !important;

                    width:
                        1.3rem !important;

                    height:
                        1.3rem !important;

                    max-width:
                        none !important;

                    max-height:
                        none !important;

                    margin:
                        0 !important;

                    padding:
                        0 !important;

                    color:
                        #0a0a0a !important;

                    fill:
                        none !important;

                    stroke:
                        #0a0a0a !important;

                    stroke-width:
                        1.6 !important;

                    stroke-linecap:
                        round !important;

                    stroke-linejoin:
                        round !important;
                }


                .abf-band .abf-social-icons
                .abf-icon svg
                .abf-icon-fill {
                    fill:
                        #0a0a0a !important;

                    stroke:
                        none !important;
                }


                /* =========================================
                   SOCIAL HOVER
                ========================================= */

                .abf-band .abf-social-icons
                .abf-icon:hover {
                    background:
                        #0a0a0a !important;

                    color:
                        #ffffff !important;

                    transform:
                        translateY(-2px);
                }


                .abf-band .abf-social-icons
                .abf-icon:hover svg {
                    color:
                        #ffffff !important;

                    stroke:
                        #ffffff !important;
                }


                .abf-band .abf-social-icons
                .abf-icon:hover svg
                .abf-icon-fill {
                    fill:
                        #ffffff !important;
                }


                /* =========================================
                   ASSOCIATION ICONS
                ========================================= */

                .abf-association-icons
                .abf-icon {
                    cursor:
                        default;
                }


                .abf-association-icons
                .abf-icon:hover {
                    background:
                        #ffffff !important;

                    color:
                        #0a0a0a !important;

                    transform:
                        none !important;
                }


                .abf-association-icons
                .abf-icon svg {
                    display:
                        block !important;

                    width:
                        1.25rem !important;

                    height:
                        1.25rem !important;

                    margin:
                        0 !important;

                    padding:
                        0 !important;

                    fill:
                        none !important;

                    stroke:
                        #0a0a0a !important;

                    stroke-width:
                        1.45 !important;

                    stroke-linecap:
                        round !important;

                    stroke-linejoin:
                        round !important;
                }


                /* =========================================
                   BLOG
                ========================================= */

                .abf-band .abf-blog-mark {
                    display:
                        block !important;

                    color:
                        #0a0a0a !important;

                    font-family:
                        Georgia,
                        "Times New Roman",
                        serif !important;

                    font-size:
                        1.15rem !important;

                    font-weight:
                        600 !important;

                    line-height:
                        1 !important;

                    letter-spacing:
                        -0.08em;
                }


                .abf-band .abf-icon:hover
                .abf-blog-mark {
                    color:
                        #ffffff !important;
                }


                /* =========================================
                   COCO
                ========================================= */

                .abf-band .abf-coco {
                    width:
                        2.85rem !important;

                    height:
                        2.85rem !important;

                    min-width:
                        2.85rem !important;

                    min-height:
                        2.85rem !important;

                    flex:
                        0 0 2.85rem !important;

                    display:
                        inline-flex !important;

                    align-items:
                        center !important;

                    justify-content:
                        center !important;

                    flex-direction:
                        column;

                    gap:
                        0.05rem;

                    margin:
                        0 !important;

                    padding:
                        0 !important;

                    border:
                        1px solid #0a0a0a !important;

                    border-radius:
                        50% !important;

                    background:
                        #ffffff !important;

                    color:
                        #0a0a0a !important;

                    cursor:
                        pointer;

                    overflow:
                        hidden !important;

                    box-sizing:
                        border-box !important;

                    position:
                        relative;

                    z-index:
                        1;

                    appearance:
                        none;

                    -webkit-appearance:
                        none;

                    transition:
                        background-color
                        0.25s ease,
                        color
                        0.25s ease,
                        transform
                        0.25s ease;
                }


                .abf-band .abf-coco:hover {
                    background:
                        #0a0a0a !important;

                    color:
                        #ffffff !important;

                    transform:
                        translateY(-2px);
                }


                .abf-coco span {
                    display:
                        block;

                    color:
                        inherit;

                    font-family:
                        Arial,
                        sans-serif !important;

                    font-size:
                        0.58rem;

                    font-weight:
                        600;

                    line-height:
                        1;

                    letter-spacing:
                        0.06em;

                    text-transform:
                        uppercase;
                }


                .abf-coco svg {
                    display:
                        block !important;

                    width:
                        0.9rem !important;

                    height:
                        0.9rem !important;

                    margin:
                        0 !important;

                    padding:
                        0 !important;

                    fill:
                        none !important;

                    stroke:
                        currentColor !important;

                    stroke-width:
                        1.6 !important;

                    stroke-linecap:
                        round !important;

                    stroke-linejoin:
                        round !important;
                }


                /* =========================================
                   CLOSING
                ========================================= */

                .abf-closing {
                    padding:
                        clamp(
                            7rem,
                            9vw,
                            10rem
                        )
                        2.5rem
                        3.5rem;

                    background:
                        var(--abf-black);

                    color:
                        var(--abf-white);

                    text-align:
                        center;
                }


                .abf-founded {
                    width:
                        min(100%, 1280px);

                    margin:
                        0 auto;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.86
                        );

                    font-size:
                        clamp(
                            1.05rem,
                            1.3vw,
                            1.3rem
                        );

                    font-weight:
                        400;

                    line-height:
                        1.72;
                }


                .abf-founded a {
                    color:
                        inherit;
                }


                .abf-wordmark {
                    display:
                        block;

                    width:
                        calc(100% + 1rem);

                    height:
                        auto;

                    margin:
                        clamp(
                            6.5rem,
                            9vw,
                            10rem
                        )
                        -0.5rem
                        0;

                    overflow:
                        visible;
                }


                .abf-wordmark text {
                    fill:
                        var(--abf-white);

                    font-size:
                        194px;

                    font-weight:
                        400;
                }


                .abf-copy {
                    margin:
                        2.8rem 0 0;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.72
                        );

                    font-size:
                        0.95rem;

                    font-weight:
                        400;

                    letter-spacing:
                        0.04em;
                }


                /* =========================================
                   TABLET
                ========================================= */

                @media (max-width: 1180px) {

                    .abf-band-inner {
                        gap:
                            1.5rem;
                    }

                    .abf-divider {
                        display:
                            none;
                    }

                    .abf-group {
                        justify-content:
                            center;
                    }

                }


                /* =========================================
                   MOBILE
                ========================================= */

                @media (max-width: 749px) {

                    .abf-story {
                        padding:
                            6rem
                            1.4rem
                            7rem;
                    }


                    .abf-roll {
                        min-height:
                            7rem;

                        margin-bottom:
                            5rem;
                    }


                    .abf-line {
                        align-items:
                            center;

                        padding:
                            0 0.5rem;

                        font-size:
                            clamp(
                                1.65rem,
                                8vw,
                                2.25rem
                            );

                        line-height:
                            1.2;

                        white-space:
                            normal;
                    }


                    .abf-intro p {
                        font-size:
                            1.08rem;

                        line-height:
                            1.65;
                    }


                    .abf-intro p + p {
                        margin-top:
                            2rem;
                    }


                    .abf-band {
                        padding:
                            2.5rem
                            1.2rem;
                    }


                    .abf-band-inner {
                        width:
                            100%;

                        max-width:
                            100%;

                        flex-direction:
                            column;

                        gap:
                            2rem;
                    }


                    .abf-privacy,
                    .abf-label {
                        white-space:
                            normal;

                        text-align:
                            center;
                    }


                    .abf-group {
                        width:
                            100%;

                        flex-direction:
                            column;

                        justify-content:
                            center;

                        gap:
                            1rem;
                    }


                    .abf-icons {
                        width:
                            100%;

                        justify-content:
                            center;

                        flex-wrap:
                            wrap;

                        gap:
                            0.65rem;
                    }


                    .abf-band .abf-icon {
                        width:
                            2.75rem !important;

                        height:
                            2.75rem !important;

                        min-width:
                            2.75rem !important;

                        min-height:
                            2.75rem !important;

                        flex:
                            0 0 2.75rem !important;
                    }


                    .abf-band .abf-coco {
                        width:
                            2.85rem !important;

                        height:
                            2.85rem !important;

                        min-width:
                            2.85rem !important;

                        min-height:
                            2.85rem !important;

                        flex:
                            0 0 2.85rem !important;

                        margin:
                            0 !important;
                    }


                    .abf-closing {
                        padding:
                            6rem
                            1.4rem
                            3rem;
                    }


                    .abf-founded {
                        font-size:
                            1.02rem;

                        line-height:
                            1.65;
                    }


                    .abf-wordmark {
                        width:
                            calc(100% + 1.8rem);

                        margin:
                            5.5rem
                            -0.9rem
                            0;
                    }


                    .abf-copy {
                        margin-top:
                            1.8rem;

                        font-size:
                            0.85rem;
                    }

                }


                /* =========================================
                   REDUCED MOTION
                ========================================= */

                @media (
                    prefers-reduced-motion: reduce
                ) {

                    .abf-line {
                        transform:
                            none;

                        transition:
                            none;
                    }

                    .abf-icon,
                    .abf-coco {
                        transition:
                            none;
                    }

                }

            `}</style>
        </>
    );
};

export default ABFooter;